//store.ts
import create from 'zustand';
import { devtools } from 'zustand/middleware';

interface TimeStampedValue {
    REFYEAR: number;
    REFMONTH: number;
}

interface DollarValue extends TimeStampedValue {
    dollarPerUnit: number;
    calculatedTotal: number;
    ProductName?: string;
}


interface DollarValues {
    [key: string]: DollarValue[];
}

interface NodeTotal extends TimeStampedValue {
    total: number;
}

interface NodeTotals {
    [nodeId: string]: NodeTotal[];
}

interface PriceHistoryItem extends TimeStampedValue {
    CMDCODE: string;
    FLOWCODE: string;
    TOTALVALUE: number;
    TOTALWEIGHT: number;
    AVERAGEUNITVALUE: number;
    isLatest: boolean;
}

interface PriceHistory {
    [CMDCODE: string]: PriceHistoryItem[];
}

interface ElectricityPriceRecord extends TimeStampedValue {
    AVG_ISO_VALUE: number;
    AVG_VALUE_IN_EUR: number;
    AVG_VALUE_IN_USD: number;
    isLatest: boolean;
}

interface CountryElectricityPrices {
    [ISO_COUNTRY2: string]: ElectricityPriceRecord[];
}

interface ParentChildMap {
    [childId: string]: {
        parentId: string;
        subInputId: string;
    };
}





interface AppState {
    flowID: string;
    optionalProcessID: string;
    processFlowKey: number;
    dollarValues: DollarValues;
    overallTotal: number;
    parentChildMap: ParentChildMap;
    nodeTotals: NodeTotals; // Add this to keep track of each node's overallTotal
    setFlowID: (id: string) => void;
    setOptionalProcessID: (id: string) => void;
    incrementProcessFlowKey: () => void;
    setDollarValue: (inputId: string, value: DollarValue) => void;
    calculateNodeTotal: (nodeId: string, REFYEAR: number, REFMONTH: number) => void;
    calculateOverallTotal: () => void;
    setParentChildRelation: (childNodeId: string, parentId: string, subInputId: string) => void;
    updateParentSubInputValue: (parentId: string, subInputId: string, newTotal: number) => void;
    priceHistory: PriceHistory;
    setPriceHistory: (CMDCODE: string, data: PriceHistoryItem[]) => void;
    electricityPrices: CountryElectricityPrices;
    setElectricityPrices: (ISO_COUNTRY2: string, newData: ElectricityPriceRecord[]) => void;
    selectedYear: number;
    selectedMonth: number;
    setSelectedYear: (year: number) => void;
    setSelectedMonth: (month: number) => void;
 

}

const filterDataByTimeRange = <T extends TimeStampedValue>(data: T[], years: number, months: number): T[] => {
    const currentDate = new Date();
    const pastDate = new Date(new Date().setMonth(currentDate.getMonth() - (years * 12 + months)));
    return data.filter(item => {
        const itemDate = new Date(item.REFYEAR, item.REFMONTH - 1);
        return itemDate >= pastDate && itemDate <= currentDate;
    });
};

export const useStore = create<AppState>((set, get) => ({

    flowID: '1248',
    optionalProcessID: '495740',
    processFlowKey: 0,
    dollarValues: {},
    overallTotal: 0,
    parentChildMap: {},
    nodeTotals: {}, // Initialize nodeTotals
    priceHistory: {},
    electricityPrices: {},
    selectedYear: new Date().getFullYear(), // Default to current year
    selectedMonth: new Date().getMonth() + 1, // Default to current month (1-12)

    setSelectedYear: (year) => set((state) => {
        // After setting the new year, calculate node totals for all nodes
        Object.keys(state.nodeTotals).forEach(nodeId => {
            state.calculateNodeTotal(nodeId, year, state.selectedMonth);
        });

        return { selectedYear: year };
    }),

    setSelectedMonth: (month) => set((state) => {
        // After setting the new month, calculate node totals for all nodes
        Object.keys(state.nodeTotals).forEach(nodeId => {
            state.calculateNodeTotal(nodeId, state.selectedYear, month);
        });

        return { selectedMonth: month };
    }),


    setFlowID: (id) => {
        console.log(`Setting flowID to ${id}`);
        set(() => ({ flowID: id }));
    },

    setOptionalProcessID: (id) => set(() => ({ optionalProcessID: id })),
    incrementProcessFlowKey: () => set((state) => ({ processFlowKey: state.processFlowKey + 1 })),
    setDollarValue: (inputId, newValue) => {
        set((state) => {
            let existingValues = state.dollarValues[inputId] || [];
            const index = existingValues.findIndex(v => v.REFYEAR === newValue.REFYEAR && v.REFMONTH === newValue.REFMONTH);

            if (index !== -1) {
                // Entry for the specific time period exists, update it
                existingValues[index] = { ...existingValues[index], ...newValue };
            } else {
                // No entry for the specific time period, add a new one
                existingValues = [...existingValues, newValue];
            }


            console.log(`Updated dollarValues for ${inputId}: `, state.dollarValues[inputId]);
            return {
                ...state,
                dollarValues: {
                    ...state.dollarValues,
                    [inputId]: existingValues,
                },
            };
        });
    },

    calculateNodeTotal: (nodeId: string, REFYEAR: number, REFMONTH: number) => {
        set((state) => {
            // Aggregate dollar values for all entries that start with the nodeId
            // Inside your set function in calculateNodeTotal or similar context
            const allRelevantDollarValues: DollarValue[] = Object.entries(state.dollarValues)
                .reduce((acc: DollarValue[], [key, values]) => { // Explicitly type acc as DollarValue[]
                    if (key.startsWith(nodeId)) {
                        const filteredValues = values.filter(value =>
                            value.REFYEAR === REFYEAR && value.REFMONTH === REFMONTH
                        );
                        acc.push(...filteredValues);
                    }
                    return acc;
                }, []); // Initialize acc as DollarValue[] explicitly if needed


            // Calculate the total for all relevant and filtered values
            const totalForSelectedPeriod = allRelevantDollarValues.reduce((sum, { calculatedTotal }) => sum + calculatedTotal, 0);

            // Log the calculated total for diagnostics
            console.log(`Calculating total for node ${nodeId} (or its children) for ${REFYEAR}-${REFMONTH}: $${totalForSelectedPeriod}`);

            // Update or add the total for the nodeId for the specific REFYEAR and REFMONTH
            const existingTotals = state.nodeTotals[nodeId] || [];
            const index = existingTotals.findIndex(nt => nt.REFYEAR === REFYEAR && nt.REFMONTH === REFMONTH);

            if (index >= 0) {
                // Update the existing total if found
                existingTotals[index].total = totalForSelectedPeriod;
            } else {
                // Otherwise, add a new total entry
                existingTotals.push({ REFYEAR, REFMONTH, total: totalForSelectedPeriod });
            }

            // Update the state with the new totals
            return {
                ...state,
                nodeTotals: {
                    ...state.nodeTotals,
                    [nodeId]: existingTotals,
                },
            };
        });
    },




    calculateOverallTotal: () => {
        set((state) => {
            const overallTotal = Object.entries(state.dollarValues).reduce((totalSum, [, dollarValuesArray]) => {
                return totalSum + dollarValuesArray.reduce((sum, { calculatedTotal }) => sum + calculatedTotal, 0);
            }, 0);
            console.log(`Calculating overall total: $${overallTotal}`);
            return { ...state, overallTotal };
        });
    },

    setParentChildRelation: (childNodeId, parentId, subInputId) => {
        set((state) => {
            // Log inside the updater function before returning the state update
            console.log(`setParentChildRelation: Setting parent ${parentId} for child ${childNodeId} with subInputId ${subInputId}`);

            return {
                parentChildMap: {
                    ...state.parentChildMap,
                    [childNodeId]: { parentId, subInputId },
                },
            };
        });
    },



    updateParentSubInputValue: (parentId, subInputId, newTotal) => set((state) => {
        const parentKey = Object.keys(state.dollarValues).find(key =>
            key.startsWith(`${parentId}`) && key.includes(subInputId)
        );

        if (parentKey) {
            // Access the most recent DollarValue object
            const values = state.dollarValues[parentKey];
            const mostRecentValue = values[values.length - 1];

            if (mostRecentValue) {
                const updatedDollarValues = [...values.slice(0, -1), {
                    ...mostRecentValue,
                    dollarPerUnit: newTotal, // Assuming you meant to update this with 'newTotal'
                    // Keep 'calculatedTotal' the same or recalculate as necessary
                }];

                console.log(`Updating parent ${parentKey} with new dollar per unit: ${newTotal}`);
                return {
                    ...state,
                    dollarValues: {
                        ...state.dollarValues,
                        [parentKey]: updatedDollarValues,
                    },
                };
            }
        }
        return state; // No update case
    }),


    setPriceHistory: (CMDCODE, data) => {
        set((state) => ({
            ...state,
            priceHistory: {
                ...state.priceHistory,
                [CMDCODE]: data,
            },
        }));
    },

    setElectricityPrices: (ISO_COUNTRY2, newData) => {
        set((state) => ({
            ...state,
            electricityPrices: {
                ...state.electricityPrices,
                [ISO_COUNTRY2]: newData,
            },
        }));
    },





}));
